import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, EmptyState, Pagination, SearchInput, Segmented, TableSkeleton } from "../../../components/ui";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { apiError, deleteComment, getModerationComments, postOfficial, setHearted, setPinned } from "../../../services/comments";
import { can } from "../../../utils/roles";
import profileImage from "../../../assets/images/user/profile-default.png";
import "./comments.css";

const SITE_URL = "https://afomedia.com";
const PAGE_SIZE = 25;
const MAX_LENGTH = 2000;

const FILTERS = [
  { value: "all", label: "All" },
  { value: "pinned", label: "Pinned" },
  { value: "hearted", label: "Hearted" },
  { value: "official", label: "Alain Foka" },
  { value: "thanks", label: "Thanks (paid)" },
  { value: "premium", label: "Premium" },
];

const formatWhen = (date, lang) =>
  date ? new Date(date).toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const formatMoney = (amount, currency) => {
  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency, currencyDisplay: "narrowSymbol", maximumFractionDigits: Number.isInteger(amount) ? 0 : 2 }).format(amount);
  } catch (error) {
    return `${amount} ${currency}`;
  }
};

/** Text area to write as Alain Foka (message or reply). */
export const OfficialComposer = ({ placeholder, submitLabel, onSubmit, onCancel, withPin = false, autoFocus = false }) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [pin, setPin] = useState(false);
  const [sending, setSending] = useState(false);
  const trimmed = text.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!trimmed || sending) return;
    setSending(true);
    const done = await onSubmit(trimmed, pin);
    setSending(false);
    if (done) {
      setText("");
      setPin(false);
    }
  };

  return (
    <form className="cm-composer" onSubmit={submit}>
      <img src="/alain_foka.png" alt="" className="cm-avatar cm-avatar-official" onError={(e) => { e.currentTarget.src = profileImage; }} />
      <div className="cm-composer-body">
        <textarea
          value={text}
          rows={autoFocus ? 2 : 3}
          maxLength={MAX_LENGTH}
          autoFocus={autoFocus}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
        />
        <div className="cm-composer-actions">
          {withPin ? (
            <label className="cm-check">
              <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} />
              {t("Pin this message")}
            </label>
          ) : <span />}
          <div className="cm-composer-buttons">
            {onCancel && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
                {t("Cancel")}
              </button>
            )}
            <button type="submit" className="btn btn-sm btn-primary" disabled={!trimmed || sending}>
              <i className="ri-checkbox-circle-fill" aria-hidden="true"></i> {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

/** One comment (or reply) with the team actions. */
const CommentRow = ({ item, isReply, canEdit, showContent, onChanged, onReply, onDelete }) => {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [replying, setReplying] = useState(false);
  const name = item.official ? "Alain Foka" : `${item.user?.name || ""} ${item.user?.last_name || ""}`.trim() || t("Deleted user");

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      if (success) toast.success(success);
      onChanged();
    } catch (error) {
      toast.error(apiError(error) || t("Action failed, please try again"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={`cm-item${isReply ? " cm-item-reply" : ""}${item.official ? " cm-item-official" : ""}${item.pinned ? " cm-item-pinned" : ""}`}
      aria-label={`${name} : ${String(item.comment || "").slice(0, 60)}`}
    >
      <img
        src={item.official ? "/alain_foka.png" : item.user?.avatar || profileImage}
        alt=""
        className={`cm-avatar${item.official ? " cm-avatar-official" : ""}`}
        onError={(e) => { e.currentTarget.src = profileImage; }}
      />
      <div className="cm-body">
        {item.pinned && (
          <p className="cm-pinned"><i className="las la-thumbtack" aria-hidden="true"></i> {t("Pinned by Alain Foka")}</p>
        )}
        <div className="cm-meta">
          <strong>{name}</strong>
          {item.official && <Badge color="blue"><i className="ri-checkbox-circle-fill" aria-hidden="true"></i> {t("Official")}</Badge>}
          {!item.official && item.user?.isPremium && <Badge color="amber">Premium</Badge>}
          {item.thanks && <Badge color="green">{t("Thanks (paid)")} · {formatMoney(item.thanks.amount, item.thanks.currency)}</Badge>}
          {item.user?.isBanned && <Badge color="red">{t("Banned")}</Badge>}
          {item.hearted && (
            <span className="cm-heart-mark" title={t("Hearted by Alain Foka")}>
              <i className="ri-heart-fill" aria-hidden="true"></i>
            </span>
          )}
          {item.likesCount > 0 && (
            <span className="cm-likes" title={t("Likes")}>
              <i className="ri-thumb-up-line" aria-hidden="true"></i> {item.likesCount}
            </span>
          )}
          <span className="cm-date">{formatWhen(item.createdAt, i18n.language)}</span>
        </div>
        {showContent && item.content && (
          <p className="cm-content">
            <i className={item.content.kind === "episode" ? "ri-film-line" : "ri-video-line"} aria-hidden="true"></i>{" "}
            {item.content.url ? (
              <a href={`${SITE_URL}${item.content.url}`} target="_blank" rel="noopener noreferrer">{item.content.title}</a>
            ) : item.content.title}
          </p>
        )}
        {item.parent && (
          <p className="cm-parent">
            <i className="ri-reply-line" aria-hidden="true"></i> {t("Reply to")} <strong>{item.parent.author || "—"}</strong> : « {item.parent.comment} »
          </p>
        )}
        <p className="cm-text">{item.comment}</p>

        {canEdit && (
          <div className="cm-actions">
            {!item.official && (
            <button
              type="button"
              className={`cm-action${item.hearted ? " is-on cm-action-heart" : ""}`}
              disabled={busy}
              aria-pressed={item.hearted}
              onClick={() => run(() => setHearted(item._id, !item.hearted), item.hearted ? t("Heart removed") : t("Heart added"))}
            >
              <i className={item.hearted ? "ri-heart-fill" : "ri-heart-line"} aria-hidden="true"></i>
              {item.hearted ? t("Remove heart") : t("Heart")}
            </button>
            )}
            {!isReply && !item.parentId && (
              <button
                type="button"
                className={`cm-action${item.pinned ? " is-on" : ""}`}
                disabled={busy}
                aria-pressed={item.pinned}
                onClick={() => run(() => setPinned(item._id, !item.pinned), item.pinned ? t("Comment unpinned") : t("Comment pinned"))}
              >
                <i className="las la-thumbtack" aria-hidden="true"></i>
                {item.pinned ? t("Unpin") : t("Pin")}
              </button>
            )}
            <button type="button" className="cm-action" disabled={busy} onClick={() => setReplying(!replying)}>
              <i className="ri-reply-line" aria-hidden="true"></i> {t("Reply as Alain Foka")}
            </button>
            <button type="button" className="cm-action cm-action-danger" disabled={busy} onClick={() => onDelete(item)}>
              <i className="ri-delete-bin-line" aria-hidden="true"></i> {t("Delete")}
            </button>
          </div>
        )}

        {replying && (
          <OfficialComposer
            autoFocus
            placeholder={t("Reply to {{name}} as Alain Foka", { name })}
            submitLabel={t("Reply")}
            onCancel={() => setReplying(false)}
            onSubmit={async (text) => {
              const done = await onReply(item, text);
              if (done) setReplying(false);
              return done;
            }}
          />
        )}

        {item.replies?.length > 0 && (
          <ul className="cm-replies">
            {item.replies.map((reply) => (
              <CommentRow
                key={reply._id}
                item={{ ...reply, parentId: item._id }}
                isReply
                canEdit={canEdit}
                showContent={false}
                onChanged={onChanged}
                onReply={onReply}
                onDelete={onDelete}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
};

/**
 * Comments moderation. With `contentType` + `contentId`: the threads of one video / episode
 * (detail pages), with a composer to post as Alain Foka. Without: the latest comments of the
 * whole site, with filters and search (Comments page).
 */
const CommentModeration = ({ contentType, contentId }) => {
  const { t } = useTranslation();
  const canEdit = can("comments", "edit");
  const contentMode = Boolean(contentId);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const key = JSON.stringify({ contentType, contentId, filter, q: search, page, pageSize: PAGE_SIZE });
  const load = useCallback(() => getModerationComments(JSON.parse(key)).then(setData), [key]);
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const reply = async (item, text) => {
    try {
      await postOfficial({ parentId: item.parentId || item._id, comment: text });
      toast.success(t("Reply published as Alain Foka"));
      load();
      return true;
    } catch (error) {
      toast.error(apiError(error) || t("Action failed, please try again"));
      return false;
    }
  };

  const publish = async (text, pin) => {
    try {
      await postOfficial({ contentType, contentId, comment: text, pin });
      toast.success(t("Message published as Alain Foka"));
      setPage(1);
      load();
      return true;
    } catch (error) {
      toast.error(apiError(error) || t("Action failed, please try again"));
      return false;
    }
  };

  const confirmDelete = async () => {
    const item = deleting;
    setDeleting(null);
    try {
      const result = await deleteComment(item._id);
      toast.success(t("commentsDeleted", { count: result?.deleted || 1 }));
      load();
    } catch (error) {
      toast.error(apiError(error) || t("Action failed, please try again"));
    }
  };

  const items = data?.items || [];

  return (
    <div className="cm">
      {contentMode && canEdit && (
        <section className="ui-card cm-publish" aria-label={t("Post a message as Alain Foka")}>
          <p className="cm-publish-title">{t("Post a message as Alain Foka")}</p>
          <OfficialComposer withPin placeholder={t("Your message, shown with the Alain Foka badge…")} submitLabel={t("Publish")} onSubmit={publish} />
        </section>
      )}

      <div className="cm-toolbar">
        <Segmented
          label={t("Filter")}
          value={filter}
          onChange={(value) => { setFilter(value); setPage(1); }}
          options={FILTERS.map((option) => ({ ...option, label: t(option.label) }))}
        />
        <SearchInput value={search} onChange={(value) => { setSearch(value); setPage(1); }} placeholder={t("Search in comments")} />
      </div>

      {!data ? (
        <TableSkeleton rows={4} />
      ) : items.length === 0 ? (
        <EmptyState icon="ri-chat-3-line">{t("No comments have been added yet!")}</EmptyState>
      ) : (
        <ul className="cm-list">
          {items.map((item) => (
            <CommentRow
              key={item._id}
              item={item}
              isReply={false}
              canEdit={canEdit}
              showContent={!contentMode}
              onChanged={load}
              onReply={reply}
              onDelete={setDeleting}
            />
          ))}
        </ul>
      )}

      <Pagination page={page} pageCount={Math.ceil((data?.total || 0) / PAGE_SIZE)} total={data?.total} pageSize={PAGE_SIZE} onChange={setPage} />

      <ConfirmDialog open={Boolean(deleting)} onClose={() => setDeleting(null)} onConfirm={confirmDelete} confirmLabel={t("Delete")} danger>
        <p>{t("Delete this comment? Its replies will be deleted too. This cannot be undone.")}</p>
        {deleting && <blockquote className="cm-quote">« {String(deleting.comment).slice(0, 200)} »</blockquote>}
      </ConfirmDialog>
    </div>
  );
};

export default CommentModeration;
